import { Component, computed, effect, inject, input, model, OnInit, signal, viewChild } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { LucideChevronDown, LucideChevronLeft, LucideChevronRight, LucideDynamicIcon } from '@lucide/angular';
import {
  injectDatePickerState,
  NgpDatePicker,
  NgpDatePickerGrid,
  NgpDatePickerLabel,
  NgpDatePickerNextMonth,
  NgpDatePickerPreviousMonth,
  NgpDatePickerRowRender,
} from 'ng-primitives/date-picker';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { InlineButtonComponent } from '../../../atoms/inline-button/inline-button.component';
import { DropdownComponent, DropdownOption } from '../../../molecules/dropdown/dropdown.component';
import { DayRowsComponent } from './components/date-picker-days.component';
import { DatePickerMonthsComponent } from './components/date-picker-months.component';
import { YearRowsComponent } from './components/date-picker-years.component';
import { WeekDaysHeaderComponent } from './components/week-days-header.component';
import { DatePickerUtilsService } from './services/date-picker-utils.service';
/**
 * Basic date picker component that uses ng-primitives/date-picker for the date picker logic.
 */

export type DatePickerSelectionMode = 'month' | 'year' | 'day';
type DatePickerSelectionTrigger = 'mobile' | 'desktop';

export type DatePickerVariant = 'showEvents' | 'enableEvents';
@Component({
  selector: 'joii-date-picker',
  hostDirectives: [
    {
      directive: NgpDatePicker,
      inputs: [
        'ngpDatePickerDate: date',
        'ngpDatePickerMin: min',
        'ngpDatePickerMax: max',
        'ngpDatePickerDisabled: disabled',
      ],
      outputs: ['ngpDatePickerDateChange: dateChange'],
    },
  ],
  imports: [
    NgpDatePickerLabel,
    NgpDatePickerNextMonth,
    NgpDatePickerPreviousMonth,
    NgpDatePickerGrid,
    NgpDatePickerRowRender,
    LucideDynamicIcon,
    InlineButtonComponent,
    DropdownComponent,
    YearRowsComponent,
    DatePickerMonthsComponent,
    WeekDaysHeaderComponent,
    DayRowsComponent,
  ],
  providers: [provideValueAccessor(DatePickerComponent)],
  template: `
    <div class="date-picker-header">
      <!-- Mobile Combined Month and Year Selector -->
      <button
        class="mobile-month-year-selector"
        joii-inline-button
        [suffixIcon]="chevronDown"
        [variant]="'secondary'"
        ngpDatePickerLabel
        (click)="onMobileMonthYearSelectorClick()"
      >
        {{ label() }}
      </button>
      <div class="date-picker-header-buttons">
        @if (selectionMode() === 'day') {
          <button
            joii-inline-button
            [variant]="'secondary'"
            [disabled]="previousMonthDisabled()"
            [transparent]="true"
            [borderless]="true"
            ngpDatePickerPreviousMonth
            aria-label="previous month"
          >
            <svg [lucideIcon]="chevronLeft" [strokeWidth]="2" />
          </button>
          <button
            joii-inline-button
            [variant]="'secondary'"
            [disabled]="nextMonthDisabled()"
            [transparent]="true"
            [borderless]="true"
            ngpDatePickerNextMonth
            aria-label="next month"
          >
            <svg [lucideIcon]="chevronRight" [strokeWidth]="2" />
          </button>
        }
        @if (selectionMode() === 'year') {
          <button
            joii-inline-button
            [variant]="'secondary'"
            [disabled]="yearRowsComponent()?.isFirstPage() ?? true"
            [transparent]="true"
            [borderless]="true"
            (click)="onPreviousPage()"
          >
            <svg [lucideIcon]="chevronLeft" [strokeWidth]="2" />
          </button>
          <button
            joii-inline-button
            [variant]="'secondary'"
            [disabled]="yearRowsComponent()?.isLastPage() ?? true"
            [transparent]="true"
            [borderless]="true"
            (click)="onNextPage()"
          >
            <svg [lucideIcon]="chevronRight" [strokeWidth]="2" />
          </button>
        }
      </div>
      <!-- Desktop Month and Year Selector -->
      <joii-dropdown
        class="desktop-month-selector"
        [fullWidth]="true"
        [options]="monthOptions()"
        [(value)]="monthValue"
        placeholder="Select a month"
        (valueChange)="onMonthChange($event)"
      />
      <joii-dropdown
        class="desktop-year-selector"
        [fullWidth]="true"
        [options]="yearOptions()"
        [(value)]="yearValue"
        placeholder="Select a year"
        (valueChange)="onYearChange($event)"
      />
    </div>
    <!-- Calendar Body -->
    <div ngpDatePickerGrid>
      @switch (selectionMode()) {
        @case ('day') {
          <joii-week-days-header />
          <joii-date-picker-days *ngpDatePickerRowRender [events]="events()" [variant]="variant()" />
        }
        @case ('month') {
          <joii-date-picker-months
            [months]="monthOptions()"
            [(month)]="monthValue"
            (monthChange)="onMonthChange($event)"
            (monthSelection)="onMonthSelection()"
          />
        }
        @case ('year') {
          <joii-date-picker-years
            [years]="yearOptions()"
            [(year)]="yearValue"
            (yearChange)="onYearChange($event)"
            (yearSelection)="onYearSelection()"
          ></joii-date-picker-years>
        }
      }
    </div>
  `,
  host: {
    '[attr.data-variant]': 'variant()',
    '(focusout)': 'onTouched?.()',
    '[attr.data-show-today]': 'showToday()',
  },
  styleUrl: './date-picker.component.css',
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  chevronLeft = LucideChevronLeft;
  chevronRight = LucideChevronRight;
  chevronDown = LucideChevronDown;
  events = input<Date[]>([]);
  variant = input<DatePickerVariant>('showEvents');
  showToday = input<boolean>(true);
  /** Access the date picker host directive */
  private readonly state = injectDatePickerState<Date>();
  private readonly dateUtils = inject(DatePickerUtilsService);

  protected selectionMode = model<DatePickerSelectionMode>('day');

  /** All twelve months, kept as one stable array so option identity survives filtering. */
  private readonly allMonthOptions: DropdownOption<number>[] = this.getMonthOptions();

  protected monthOptions = computed<DropdownOption<number>[]>(() =>
    this.getMonthOptionsInRange(this.state().focusedDate().getFullYear(), this.state().min(), this.state().max())
  );
  protected yearOptions = computed<DropdownOption<number>[]>(() =>
    this.getYearOptions(this.state().min(), this.state().max())
  );
  protected monthValue = signal<DropdownOption<number> | undefined>(
    this.getMonthOption(this.state().focusedDate().getMonth(), this.monthOptions())
  );
  protected yearValue = signal<DropdownOption<number> | undefined>(
    this.getYearOption(this.state().focusedDate().getFullYear(), this.yearOptions())
  );

  protected selectionTrigger = signal<DatePickerSelectionTrigger>('desktop');

  /**
   * Mirrors the bounds checks in `NgpDatePickerPreviousMonth` / `NgpDatePickerNextMonth` so the
   * arrows render as disabled instead of looking live while doing nothing.
   */
  protected readonly previousMonthDisabled = computed(() => {
    const state = this.state();
    const min = state.min();
    if (state.disabled()) {
      return true;
    }
    if (!min) {
      return false;
    }

    const focusedDate = state.focusedDate();
    const firstDayOfMonth = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1);
    return min.getTime() >= firstDayOfMonth.getTime();
  });

  protected readonly nextMonthDisabled = computed(() => {
    const state = this.state();
    const max = state.max();
    if (state.disabled()) {
      return true;
    }
    if (!max) {
      return false;
    }

    const focusedDate = state.focusedDate();
    const lastDayOfMonth = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return max.getTime() <= lastDayOfMonth.getTime();
  });

  yearRowsComponent = viewChild<YearRowsComponent>(YearRowsComponent);

  /**
   * Get the current focused date in string format.
   * @returns The focused date in "February 2024" format.
   */
  readonly label = computed(
    () =>
      `${this.state().focusedDate().toLocaleString('default', { month: 'long' })} ${this.state().focusedDate().getFullYear()}`
  );

  ngOnInit(): void {
    const selectedDate = this.state().date();
    if (selectedDate) {
      this.state().setFocusedDate(selectedDate, 'mouse', 'forward');
    } else if (!this.isFocusedDateValid()) {
      this.state().setFocusedDate(this.state().max() ?? new Date(), 'mouse', 'forward');
    }

    this.syncFocusedMonthYear();
  }

  /** Moves focus to the focused date's day, e.g. when the picker is opened from the keyboard. */
  focusActiveDate(): void {
    const state = this.state();
    state.setFocusedDate(state.focusedDate(), 'keyboard', 'forward');
  }

  private syncFocusedMonthYear(): void {
    const focusedDate = this.state().focusedDate();
    this.monthValue.set(this.getMonthOption(focusedDate.getMonth(), this.monthOptions()));
    this.yearValue.set(this.getYearOption(focusedDate.getFullYear(), this.yearOptions()));
  }

  protected isSelected(date: Date): boolean {
    const selectedDate = this.state().date();
    return (
      selectedDate?.getFullYear() === date.getFullYear() &&
      selectedDate?.getMonth() === date.getMonth() &&
      selectedDate?.getDate() === date.getDate()
    );
  }

  /**
   * The onChange callback function for the date picker.
   */
  protected onChange?: ChangeFn<Date | undefined>;

  /**
   * The onTouched callback function for the date picker.
   */
  protected onTouched?: TouchedFn;

  constructor() {
    // Whenever the user interacts with the date picker, call the onChange function with the new value.
    this.state().dateChange.subscribe((date) => this.onChange?.(date));

    // The grid renders the focused date, so the header selectors have to follow it rather than hold
    // their own month/year. Without this the arrows leave the selectors on the previous month.
    effect(() => {
      this.state().focusedDate();
      this.syncFocusedMonthYear();
    });
  }

  writeValue(date: Date): void {
    // writeValue is the model -> view direction, so it must not emit: the
    // emitted change would come straight back through onChange as a view -> model
    // update and loop forever.
    this.state().select(date, false, { emit: false });
  }

  registerOnChange(fn: ChangeFn<Date | undefined>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.state().setDisabled(isDisabled);
  }

  getMonthOptions(): DropdownOption<number>[] {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      name: new Date(0, i).toLocaleString('default', { month: 'long' }),
    }));
  }

  /**
   * The months of `year` that contain at least one selectable day. The bounds checks match the day
   * button's, which compares the start of each day against min/max.
   */
  getMonthOptionsInRange(year: number, min?: Date, max?: Date): DropdownOption<number>[] {
    return this.allMonthOptions.filter((option) => {
      const monthIndex = option.value - 1;
      const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
      const firstDayOfMonth = new Date(year, monthIndex, 1);

      if (min && lastDayOfMonth.getTime() < min.getTime()) {
        return false;
      }
      if (max && firstDayOfMonth.getTime() > max.getTime()) {
        return false;
      }
      return true;
    });
  }

  getYearOptions(min?: Date, max?: Date): DropdownOption<number>[] {
    const minYear = min ? min.getFullYear() : new Date().getFullYear() - 100;
    const maxYear = max ? max.getFullYear() : new Date().getFullYear();
    const totalYears = maxYear - minYear + 1;
    return Array.from({ length: totalYears }, (_, i) => ({
      value: minYear + i,
      name: (minYear + i).toString(),
    })).sort((a, b) => a.value - b.value);
  }

  getMonthOption(month: number, monthOptions: DropdownOption<number>[]): DropdownOption<number> {
    // Assumes month is in range 0-11, with 0 = January, 11 = December
    return (
      monthOptions.find((option) => option.value === month + 1) ?? {
        value: month + 1,
        name: new Date(0, month).toLocaleString('default', { month: 'long' }),
      }
    );
  }

  getYearOption(year: number, yearOptions: DropdownOption<number>[]): DropdownOption<number> {
    return (
      yearOptions.find((option) => option.value === year) ?? {
        value: year,
        name: year.toString(),
      }
    );
  }

  onMonthChange(monthOption: DropdownOption<number> | undefined): void {
    if (!monthOption || !this.yearValue()) {
      return;
    }
    this.state().setFocusedDate(new Date(this.yearValue()!.value, monthOption.value - 1, 1), 'mouse', 'forward');
    // `setFocusedDate` clamps to min/max, and a clamp onto the bound itself leaves `focusedDate`
    // unchanged, so the effect would not fire and the selector would keep the rejected month.
    this.syncFocusedMonthYear();
    if (this.selectionTrigger() === 'mobile') {
      this.selectionMode.set('day');
      this.selectionTrigger.set('desktop');
    }
  }

  onYearChange(yearOption: DropdownOption<number> | undefined): void {
    if (!yearOption || !this.monthValue()) {
      return;
    }
    this.state().setFocusedDate(new Date(yearOption.value, this.monthValue()!.value - 1, 1), 'mouse', 'forward');
    this.syncFocusedMonthYear();
    this.onYearSelection();
  }

  onYearSelection(): void {
    if (this.selectionTrigger() === 'mobile') {
      this.selectionMode.set('month');
    }
  }

  onMonthSelection(): void {
    if (this.selectionTrigger() === 'mobile') {
      this.selectionMode.set('day');
      this.selectionTrigger.set('desktop');
    }
  }

  onMobileMonthYearSelectorClick(): void {
    this.selectionMode.set('year');
    this.selectionTrigger.set('mobile');
  }

  isFocusedDateValid(): boolean {
    const state = this.state();
    const focusedDate = state.focusedDate();
    const min = state.min();
    const max = state.max();
    if (!focusedDate) return false;
    if (min && focusedDate < min) return false;
    if (max && focusedDate > max) return false;
    return true;
  }

  onNextPage(): void {
    this.yearRowsComponent()?.nextPage();
  }

  onPreviousPage(): void {
    this.yearRowsComponent()?.previousPage();
  }
}
